import { z } from 'zod';

/**
 * The dispatch board's "On leave" column — home leave filed UNTIL RETURN.
 *
 * Dropping workers on the column files an APPROVED annual leave for each, from
 * the board's day with no end. Sending one of them to a site (or back to camp)
 * ends it on the day before. While open, the leave's `endDate` is this
 * sentinel: every reader that asks "does a leave cover this day" keeps working
 * unchanged, and a screen recognises it to say "until return" instead of a date.
 */
export const OPEN_LEAVE_END = '9999-12-31';

/** True when a leave's end (`YYYY-MM-DD…`) is the open sentinel. */
export function isOpenLeaveEnd(endDate: string | null | undefined): boolean {
  return typeof endDate === 'string' && endDate.slice(0, 10) === OPEN_LEAVE_END;
}

/** A board-filed leave is at most a year long when it has an end. */
export const DISPATCH_LEAVE_MAX_DAYS = 366;

/**
 * Put these workers on leave from the board's day — in the day's DRAFT; the
 * leave is filed when the day is published. Clear day's cap: one request takes
 * a whole roster's paste. With neither `days` nor `endDate` it is open — until
 * return. With either, it is an ordinary approved annual leave ending then
 * (`days` counts calendar days, the board's day being the first), charged when
 * filed, as any approved request is.
 */
export const DispatchLeaveSchema = z
  .object({
    employeeIds: z.array(z.string().min(1)).min(1).max(2000),
    days: z.number().int().min(1).max(DISPATCH_LEAVE_MAX_DAYS).optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').optional(),
    /** The type it is filed as — an undo putting a draft back as it was. Absent or null: annual. */
    leaveTypeId: z.string().min(1).nullable().optional(),
  })
  .refine((v) => v.days === undefined || v.endDate === undefined, 'Give a number of days or an end date, not both');

/** The reason a board-filed leave WITH an end carries. Text only: the board knows its own leave by `filedFrom`. */
export const BOARD_FIXED_LEAVE_REASON = 'Home leave, from the dispatch board';
export type DispatchLeave = z.infer<typeof DispatchLeaveSchema>;

/**
 * The end date of a leave in the On leave box: the LAST day of leave — the
 * board says "Until <date>" — and on the day after it the worker is in camp
 * again, and absent without a punch. null takes the date away: until return
 * again. A FILED leave is named by `leaveRequestId`; a leave still in the
 * day's draft (not yet published) by `employeeId`.
 */
export const DispatchLeaveEndSchema = z
  .object({
    leaveRequestId: z.string().min(1).optional(),
    employeeId: z.string().min(1).optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD').nullable(),
  })
  .refine((v) => (v.leaveRequestId === undefined) !== (v.employeeId === undefined), 'Name a leave request or a worker, not both');
export type DispatchLeaveEnd = z.infer<typeof DispatchLeaveEndSchema>;

/**
 * The type of a leave still in the day's draft, named by its worker: the type
 * Publish files it as. null: the company's annual type, the default. A filed
 * leave keeps the type it was filed as.
 */
export const DispatchLeaveTypeSchema = z.object({
  employeeId: z.string().min(1),
  leaveTypeId: z.string().min(1).nullable(),
});
export type DispatchLeaveType = z.infer<typeof DispatchLeaveTypeSchema>;

/** "End leave" in the On leave box, for a leave filed elsewhere: the board's day becomes the first day back. */
export const DispatchLeaveEndOtherSchema = z.object({
  leaveRequestId: z.string().min(1),
});
export type DispatchLeaveEndOther = z.infer<typeof DispatchLeaveEndOtherSchema>;

/** Undo a send-to-leave or a return: the leave requests the write answered with. */
export const DispatchLeaveUndoSchema = z.object({
  leaveRequestIds: z.array(z.string().min(1)).min(1).max(2000),
});
export type DispatchLeaveUndo = z.infer<typeof DispatchLeaveUndoSchema>;

/**
 * Undo a send-to-leave: the workers whose leave is still a DRAFT (nothing was
 * filed — the draft is dropped), and any leave requests it did file.
 */
export const DispatchLeaveSendUndoSchema = z
  .object({
    leaveRequestIds: z.array(z.string().min(1)).max(2000).default([]),
    employeeIds: z.array(z.string().min(1)).max(2000).default([]),
  })
  .refine((v) => v.leaveRequestIds.length + v.employeeIds.length > 0, 'Nothing to undo');
export type DispatchLeaveSendUndo = z.infer<typeof DispatchLeaveSendUndoSchema>;

/** HR ends an open leave from the leave screen: the first day back at work. */
export const LeaveReturnSchema = z.object({
  returnDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD'),
});
export type LeaveReturn = z.infer<typeof LeaveReturnSchema>;
